import { EntityType } from '@frontend/common';
import { Functionality } from '@frontend/functionality/types';
import { useDocumentRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveUsersByGroupMemberOrder } from '../../utils/resolve-users-by-group-member-order';
import { useScreen } from '../useScreen';

const isOtherNamedUser = (user: IUser): boolean => {
    const firstName = user.first_name?.trim().toLowerCase();
    if (firstName === 'other') return true;
    return new User(user).getDisplayName().trim().toLowerCase() === 'other';
};

interface ViewProps {
    functionality?: Functionality;
    isLoading?: boolean;
    couriers: IUser[];
    selectedCourier?: IUser | null;
    isOtherSelected: boolean;
    canContinue: boolean;
    showOtherModal: boolean;
    otherName: string;
    setOtherName: (value: string) => void;
    closeOtherModal: () => void;
    onOtherModalSubmit: () => void;
    onOtherClick: () => void;
    onCourierClick: (courier: IUser) => void;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
}

const useSelectCourier = (): ViewProps => {
    const documentRepository = useDocumentRepository();
    const { store, onBack, onSubmit: onContinue, updateWizardData, navigationState } = useScreen();
    const { transaction, syncTransaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: true
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [couriers, setCouriers] = useState<IUser[]>([]);
    const [selected, setSelected] = useState<IUser | null>(null);
    const [isOtherSelected, setIsOtherSelected] = useState(false);
    const [matchedOtherUser, setMatchedOtherUser] = useState<IUser | null>(null);
    const [showOtherModal, setShowOtherModal] = useState(false);
    const [otherName, setOtherName] = useState('');

    const remoteSendersRef = useRef<TransactionUser[]>([]);
    const hasAutoContinuedRef = useRef(false);
    const loggedInUserId = navigationState.user?.id;

    const loadCourierOptions = async () => {
        const courierUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_SENDERS_USER_GROUP_ID)?.value;
        if (!courierUserGroupId || typeof courierUserGroupId !== 'string') {
            setCouriers([]);
            setMatchedOtherUser(null);
            setIsLoading(false);
            return;
        }

        try {
            const orderedCouriers = await resolveUsersByGroupMemberOrder(courierUserGroupId);
            const otherUser = orderedCouriers.find(isOtherNamedUser) ?? null;
            setMatchedOtherUser(otherUser);

            const displayCouriers = orderedCouriers.filter((user) => !isOtherNamedUser(user));
            await documentRepository.preLoadFiles(
                EntityType.USER,
                displayCouriers.map((user) => user.id)
            );
            setCouriers(displayCouriers);

            const existingSenders = (store?.data?.senders as TransactionUser[]) || [];

            if (transaction?.id) {
                const fetchedSenders = await TransactionUserClient.fetchTransactionUsers({
                    transaction_id: transaction.id,
                    type: TransactionUserType.SENDER
                });
                remoteSendersRef.current = fetchedSenders.results;

                if (existingSenders.length === 0 && fetchedSenders.results.length > 0) {
                    updateWizardData({
                        ...store?.data,
                        senders: fetchedSenders.results
                    });
                }
            }

            const foundSender = orderedCouriers.find((u) => existingSenders.some((s) => u.id === s.user_id));
            if (foundSender && isOtherNamedUser(foundSender)) {
                setIsOtherSelected(true);
                setSelected(foundSender);
            } else if (foundSender) {
                setSelected(foundSender);
                setIsOtherSelected(false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const loadCourierOptionsRef = useRef(loadCourierOptions);
    loadCourierOptionsRef.current = loadCourierOptions;

    useEffect(() => {
        if (loggedInUserId) return;
        void loadCourierOptionsRef.current();
    }, [loggedInUserId]);

    const buildSender = (userId: string): Partial<TransactionUser> => ({
        user_id: userId,
        type: TransactionUserType.SENDER,
        transaction_id: transaction?.id || store?.data?.transaction?.id || '',
        account_id: store?.wizard.account_id ?? '',
        creation_timestamp: new Date().toISOString(),
        update_timestamp: new Date().toISOString()
    });

    const writeSender = (courier: IUser | null) => {
        if (!store) return;
        updateWizardData({
            ...store.data,
            senders: courier ? [buildSender(courier.id)] : []
        });
    };

    const setSelectedCourier = (courier: IUser | null) => {
        setSelected(courier);
        setIsOtherSelected(false);
        writeSender(courier);
    };

    const selectOther = () => {
        setIsOtherSelected(true);
        if (matchedOtherUser) {
            setSelected(matchedOtherUser);
            writeSender(matchedOtherUser);
        } else {
            setSelected(null);
            writeSender(null);
        }
    };

    const syncSenders = async (localSendersOverride?: Partial<TransactionUser>[]) => {
        if (!store || !transaction?.id) return;

        const accountId = store.wizard.account_id;
        const transactionId = transaction.id;
        const remoteSenders = remoteSendersRef.current;
        const localSenders = localSendersOverride ?? ((store.data?.senders as TransactionUser[]) || []);

        const sendersToAdd = localSenders.filter((local) => !remoteSenders.find((remote) => remote.user_id === local.user_id));
        const sendersToRemove = remoteSenders.filter((remote) => !localSenders.find((local) => local.user_id === remote.user_id));

        const addedSenders: TransactionUser[] = [];
        for (const sender of sendersToAdd) {
            const addedSender = await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: sender.user_id,
                type: TransactionUserType.SENDER
            });
            addedSenders.push(addedSender);
        }

        for (const sender of sendersToRemove) {
            await TransactionUserClient.deleteTransactionUser(accountId, transactionId, sender.user_id);
        }

        remoteSendersRef.current = [...addedSenders, ...remoteSenders.filter((remote) => localSenders.some((local) => local.user_id === remote.user_id))];
    };

    const advance = () => {
        if (!store) return;
        const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_COURIER && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    };

    useEffect(() => {
        if (!loggedInUserId || !store || !transaction?.id || hasAutoContinuedRef.current) return;
        hasAutoContinuedRef.current = true;

        const autoContinueAsLoggedInSender = async () => {
            try {
                const fetchedSenders = await TransactionUserClient.fetchTransactionUsers({
                    transaction_id: transaction.id,
                    type: TransactionUserType.SENDER
                });
                remoteSendersRef.current = fetchedSenders.results;

                const sender = buildSender(loggedInUserId);
                updateWizardData({
                    ...store.data,
                    senders: [sender]
                });
                await syncSenders([sender]);
                advance();
            } catch {
                hasAutoContinuedRef.current = false;
                await loadCourierOptionsRef.current();
            }
        };

        void autoContinueAsLoggedInSender();
    }, [loggedInUserId, transaction?.id]);

    const openOtherModal = () => {
        setOtherName('');
        setShowOtherModal(true);
    };

    const closeOtherModal = () => {
        if (isLoading) return;
        setShowOtherModal(false);
    };

    const submitNormalCourier = async () => {
        if (!store || !selected || !transaction?.id || isOtherSelected) return;

        setIsLoading(true);
        try {
            await syncSenders();
            advance();
        } finally {
            setIsLoading(false);
        }
    };

    const onOtherModalSubmit = async () => {
        const trimmed = otherName.trim();
        if (!trimmed || !transaction?.id) return;

        const trackingNumber = typeof store?.data?.courierTrackingNumber === 'string' ? store.data.courierTrackingNumber.trim() : '';
        const shippingNotes = trackingNumber ? `${trimmed} - ${trackingNumber}` : trimmed;

        setIsLoading(true);
        try {
            await syncTransaction({ shipping_notes: shippingNotes });
            await syncSenders();
            setShowOtherModal(false);
            advance();
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = () => {
        if (isOtherSelected) {
            openOtherModal();
            return;
        }
        void submitNormalCourier();
    };

    const onCourierClick = (courier: IUser) => {
        if (!isOtherSelected && selected?.id === courier.id) {
            void submitNormalCourier();
            return;
        }
        setSelectedCourier(courier);
    };

    const onOtherClick = () => {
        if (isOtherSelected) {
            openOtherModal();
            return;
        }
        selectOther();
    };

    return {
        functionality: store?.functionality,
        isLoading,
        couriers,
        selectedCourier: selected,
        isOtherSelected,
        canContinue: isOtherSelected || !!selected,
        showOtherModal,
        otherName,
        setOtherName,
        closeOtherModal,
        onOtherModalSubmit,
        onOtherClick,
        onCourierClick,
        onSubmit,
        onBack
    };
};

export default useSelectCourier;
